import {useEffect,useLayoutEffect,useRef,useState,type ReactNode} from 'react';

// Catalogue data already arrives through the shared draw stream. Reveal only
// another batch near the viewport; money updates do not restart the observer.
export function LazyCardGrid<T extends {id:string}>({items,queryKey,batchSize,footerActive=false,renderCard,renderGrid}:{items:T[];queryKey:string;batchSize:number;footerActive?:boolean;renderCard:(item:T)=>ReactNode;renderGrid?:(children:ReactNode,count:number)=>ReactNode}){

 const size=Math.max(1,Math.floor(batchSize)),root=useRef<HTMLDivElement>(null),end=useRef<HTMLDivElement>(null),previous=useRef(queryKey);
 const [batch,setBatch]=useState({key:queryKey,count:size});
 const count=Math.min(items.length,batch.key===queryKey?batch.count:size),more=count<items.length;
 const reveal=()=>setBatch(current=>({key:queryKey,count:Math.min(items.length,(current.key===queryKey?current.count:size)+size)}));
 useLayoutEffect(()=>{
  if(previous.current===queryKey)return;previous.current=queryKey;setBatch({key:queryKey,count:size});
  const element=root.current;if(!element)return;
  const scroller=element.closest<HTMLElement>('[data-dialog-scroll],dialog'),toolbar=element.closest('.room-cards')?.querySelector('.room-purchase-toolbar');
  const heading=element.previousElementSibling;
  const top=(heading?.matches('.card-heading-controls')?heading:element).getBoundingClientRect().top,edge=toolbar?.getBoundingClientRect().bottom??scroller?.getBoundingClientRect().top??0;
  // Keep the list heading visible when a scrolled catalogue changes filters.
  if(top<edge){const delta=top-edge;if(scroller)scroller.scrollTop+=delta;else globalThis.scrollBy(0,delta);}
 },[queryKey,size]);
 // Opening the purchase footer must not cover the card just selected.
 // Scroll only on its first appearance; later stake changes do not move the list.
 useLayoutEffect(()=>{
  if(!footerActive)return;
  const element=root.current,card=element?.querySelector<HTMLElement>('.draft-selected');
  const dialog=element?.closest<HTMLElement>('dialog');
  const footer=(dialog??document).querySelector<HTMLElement>('.selection-tray');
  if(!card||!footer)return;
  const column=element?.closest('.room-play-column'),actions=column?.querySelector('.room-top-actions');
  if(actions){
   const rect=card.getBoundingClientRect(),toolbar=column?.querySelector('.room-purchase-toolbar');
   const top=actions.getBoundingClientRect().bottom+(toolbar?.getBoundingClientRect().height??0),bottom=Math.min(innerHeight,dialog?.getBoundingClientRect().bottom??innerHeight);
   // Desktop checkout grows above the document list. Keep the chosen card
   // below the sticky actions using the page/dialog's own scroll position.
   // A short iframe may not fit the whole card. Keep its header reachable
   // instead of revealing its bottom by pushing the header under checkout.
   const delta=rect.top<top?rect.top-top:Math.min(Math.max(0,rect.bottom-bottom),rect.top-top);
   if(delta){if(dialog)dialog.scrollTop+=delta;else globalThis.scrollBy(0,delta);}
   return;
  }
  const rect=card.getBoundingClientRect(),edge=footer.getBoundingClientRect().top;
  if(rect.top<0||rect.top>innerHeight||rect.bottom<=edge)return;
  const delta=rect.bottom-edge;
  if(dialog)dialog.scrollTop+=delta;else globalThis.scrollBy(0,delta);
 },[footerActive]);
 useEffect(()=>{
  const sentinel=end.current;if(!sentinel||!more)return;
  if(typeof IntersectionObserver==='undefined'){setBatch({key:queryKey,count:items.length});return;}
  const observer=new IntersectionObserver(entries=>{if(entries.some(entry=>entry.isIntersecting))reveal();},{root:sentinel.closest('[data-dialog-scroll],dialog'),rootMargin:"0px 0px 500px 0px"});
  observer.observe(sentinel);return()=>observer.disconnect();
 },[queryKey,count,more,items.length,size]);
 const children=items.slice(0,count).map(renderCard);
 return <div ref={root} className="lazy-card-list" data-shown={count} data-total={items.length} onFocusCapture={event=>{if(more&&event.target.closest('.bingo-card')===event.currentTarget.querySelector('.card-grid')?.lastElementChild)reveal();}}>
  {renderGrid?renderGrid(children,count):<div className="card-grid">{children}</div>}
  {items.length>0&&<div ref={end} className="card-list-sentinel" aria-hidden="true"/>}
 </div>;
}
